import { useQuery } from "../../../Utils/query";
import { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../../../config/supabaseClient";
import {
  TEMPLATE_COMPONENTS_TABLE,
  WEBSITE_TEMPLATE_SURFACE,
} from "../../../Utils/StayUpToDate/componentSurface";
import { resolvePublicComponentByRouteSlug } from "../../../Utils/StayUpToDate/resolveComponentByRouteSlug";

function slugLookupAttempts(raw: string): string[] {
  const lower = raw.toLowerCase();
  return raw === lower ? [raw] : [raw, lower];
}

function slugifyComponentLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function templateRowMatchesSlug(
  row: Record<string, unknown>,
  slug: string,
): boolean {
  const want = slug.trim().toLowerCase();
  if (!want) return false;
  const rn = String(row.route_name ?? "").trim().toLowerCase();
  const uid = String(row.unique_id ?? "").trim().toLowerCase();
  const nm = String(row.name ?? "").trim().toLowerCase();
  const slugName = slugifyComponentLabel(String(row.name ?? ""));
  return rn === want || uid === want || nm === want || slugName === want;
}

async function queryComponentRowBySlug(
  client: SupabaseClient,
  tableName: string,
  user_id: string,
  slug: string,
): Promise<any | undefined> {
  const { data } = await client
    .from(tableName)
    .select("*")
    .eq("user_id", user_id)
    .neq("status", "deleted")
    .or(`unique_id.eq.${slug},route_name.eq.${slug}`);

  const safeData = (data as any[]) || [];
  return safeData[0];
}

/** `content-series` rows often only have `name` + `query` (no route_name/unique_id). */
async function queryTemplateComponentRowBySlug(
  client: SupabaseClient,
  user_id: string,
  slug: string,
): Promise<any | undefined> {
  const s = String(slug || "").trim();
  if (!s) return undefined;

  const direct = await queryComponentRowBySlug(
    client,
    TEMPLATE_COMPONENTS_TABLE,
    user_id,
    s,
  );
  if (direct) return direct;

  const base = () =>
    client
      .from(TEMPLATE_COMPONENTS_TABLE)
      .select("*")
      .eq("user_id", user_id)
      .neq("status", "deleted");

  const { data: byName } = await base().ilike("name", s).limit(1);
  if (byName?.[0]) return byName[0];

  const { data: seriesRows } = await base()
    .eq("type", "content-series")
    .limit(100);
  for (const row of (seriesRows as Record<string, unknown>[]) ?? []) {
    if (templateRowMatchesSlug(row, s)) return row;
  }

  return undefined;
}

/** Same lookup as {@link useLoadRenderComponent} — shared for tab prefetch / filtering. */
export async function fetchStayUpToDateComponentRow(
  user_id: string,
  unique_id: string | undefined | null,
  from?: string,
  client: SupabaseClient = supabase,
  username?: string,
): Promise<any | undefined> {
  const slug = typeof unique_id === "string" ? unique_id.trim() : String(unique_id ?? "");
  const enabled =
    Boolean(slug) &&
    slug !== "undefined" &&
    (Boolean(user_id) || Boolean(username)) &&
    user_id !== "undefined";
  if (!enabled) return undefined;

  // Website template surface keeps dedicated template-components lookup.
  if (from === WEBSITE_TEMPLATE_SURFACE) {
    for (const attempt of slugLookupAttempts(slug)) {
      const row = await queryTemplateComponentRowBySlug(client, user_id, attempt);
      if (row) return row;
    }
    return undefined;
  }

  // Production public / embed / creator-page resolution (username fallback).
  return resolvePublicComponentByRouteSlug(
    client,
    slug,
    user_id,
    from,
    username,
  );
}

export function useLoadRenderComponent(
  user_id: string,
  unique_id: any,
  from?: any,
  client: SupabaseClient = supabase,
  username?: string,
) {
  const slug = typeof unique_id === "string" ? unique_id.trim() : String(unique_id ?? "");
  const enabled =
    Boolean(slug) &&
    slug !== "undefined" &&
    (Boolean(user_id) || Boolean(username)) &&
    user_id !== "undefined";

  const getComponent = async () => {
    if (!enabled) return undefined;
    return fetchStayUpToDateComponentRow(user_id, unique_id, from, client, username);
  };

  return useQuery(
    ["getComponent", user_id, username, slug, from],
    getComponent,
    {
      enabled,
      staleTime: 60_000,
    }
  );
}
